import { useCallback, useEffect, useState, RefObject } from 'react'
import useScrollWhenDragging from './useScrollWhenDragging'

interface IUseHorizontalDragDropProps<T extends HTMLElement = HTMLElement> {
  elementRef: RefObject<T | null>
  containerRef: RefObject<T | null>
  scrollableContainerRef: RefObject<HTMLElement | null>
  handleDrag?: (position: number) => void
  handleDrop?: (position: number) => void
  handleDragStart?: () => void
  handleDragEnd?: () => void
  constraints?: {
    minX?: number
    maxX?: number
  }
}

export const useHorizontalDragDrop = <T extends HTMLElement = HTMLElement>({
  elementRef,
  containerRef,
  scrollableContainerRef,
  handleDrop,
  handleDrag,
  handleDragStart,
  handleDragEnd,
  constraints = {}
}: IUseHorizontalDragDropProps<T>) => {
  const [isDragging, setIsDragging] = useState(false)

  const element = elementRef.current
  const scrollableContainer = scrollableContainerRef.current
  const container = containerRef.current

  useScrollWhenDragging({
    scrollableContainerRef,
    isDisabled: !isDragging
  })

  const getLeftOffsetInContainer = useCallback(() => {
    if (!element || !container) return 0

    const elementRect = element.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    return elementRect.left - containerRect.left
  }, [element, container])

  const startDrag = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!element || !container) return

      event.preventDefault()
      event.stopPropagation()

      setIsDragging(true)
      if (handleDragStart) handleDragStart()

      const onMove = (e: MouseEvent | TouchEvent) => {
        e.preventDefault()
        if (!element || !container || !scrollableContainer) return

        const containerRect = container.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        const elementWidth = elementRect.width
        const containerWidth = containerRect.width

        const mouseX = 'touches' in e ? e.touches[0].clientX : e.clientX

        let newLeft = mouseX - containerRect.left

        // Apply constraints if provided
        const { minX = 0, maxX = containerWidth - elementWidth } = constraints
        newLeft = Math.max(minX, Math.min(newLeft, maxX))

        // Update position
        element.style.left = `${newLeft}px`
        if (handleDrag) handleDrag(newLeft)
      }

      const onEnd = () => {
        setIsDragging(false)
        handleDragEnd?.()
        document.removeEventListener('mousemove', onMove as EventListener)
        document.removeEventListener('touchmove', onMove as EventListener)
        document.removeEventListener('mouseup', onEnd)
        document.removeEventListener('touchend', onEnd)

        const leftOffset = getLeftOffsetInContainer()

        if (handleDrop) handleDrop(leftOffset)
      }

      document.addEventListener('mousemove', onMove as EventListener)
      document.addEventListener('touchmove', onMove as EventListener, {
        passive: false
      })
      document.addEventListener('mouseup', onEnd)
      document.addEventListener('touchend', onEnd)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      element,
      container,
      scrollableContainer,
      handleDrop,
      handleDragStart,
      handleDragEnd,
      constraints
    ]
  )

  // Attach drag event handlers to the element
  useEffect(() => {
    if (!element) return

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      startDrag(e)
    }

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault()
      startDrag(e)
    }

    element.addEventListener('mousedown', handleMouseDown)
    element.addEventListener('touchstart', handleTouchStart)

    return () => {
      element.removeEventListener('mousedown', handleMouseDown)
      element.removeEventListener('touchstart', handleTouchStart)
    }
  }, [element, startDrag])

  return {
    isDragging
  }
}
